import { createConsumer, createSubscriber, getGlobalEventsLogs, IEvent } from '@cian/events-log';
import { ModalWindow, useDeviceType } from '@cian/ui-kit';
import loadable from '@loadable/component';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { LoadableErrorBoundary } from '../../components/LoadableErrorBoundary';
import { LoadableFallback } from '../../components/LoadableFallback';
import { selectExperiments } from '../../selectors/experiments';
import { selectUser } from '../../selectors/user';
import { IUserAuthenticatedState } from '../../types/user';
import { useApplicationContext } from '../../utils/applicationContext';
import { IModalController } from './types/modalController';

const FeedbackFormContainerLoadable = loadable(
  () => import(/* webpackChunkName: "FeedbackFormContainer" */ './FeedbackFormContentContainerLoadable'),
  {
    fallback: <LoadableFallback />,
    ssr: false,
  },
);

export const FeedbackFormContainer: React.FC = () => {
  const { logger, httpApi, config } = useApplicationContext();
  const [open, setOpen] = React.useState(false);
  const [modalTitle, setModalTitle] = React.useState<React.ReactText | undefined>(undefined);
  const [modalFooter, setModalFooter] = React.useState<React.ReactNode>(null);

  const modalController = React.useMemo<IModalController>(
    () => ({
      open,
      title: modalTitle,
      footer: modalFooter,
      setOpen,
      setFooter: setModalFooter,
      setTitle: setModalTitle,
    }),
    [modalFooter, modalTitle, open],
  );

  const user = useSelector(selectUser) as IUserAuthenticatedState;
  const experiments = useSelector(selectExperiments);
  const deviceType = useDeviceType();
  const isPhone = deviceType === 'phone';

  const handleRetry = React.useCallback(() => {
    FeedbackFormContainerLoadable.load();
  }, []);

  React.useEffect(() => {
    const feedbackConsumer = createConsumer({ topic: 'feedback' });

    const feedbackSubscriber = createSubscriber((event: IEvent<unknown>) => {
      if (event.type === 'open') {
        setOpen(true);

        const globalLog = getGlobalEventsLogs();

        globalLog.produce('feedback', { type: 'popup_opened', value: true });
      }
    });

    feedbackConsumer.subscribe(feedbackSubscriber);

    return () => {
      feedbackConsumer.unsubscribe(feedbackSubscriber);
      feedbackConsumer.destroy();
    };
  }, []);

  return (
    <ModalWindow
      fixed
      open={open}
      onClose={() => setOpen(false)}
      title={modalTitle}
      maxWidth={540}
      content={
        <LoadableErrorBoundary logger={logger} onRetry={handleRetry}>
          <FeedbackFormContainerLoadable
            modalController={modalController}
            logger={logger}
            config={config}
            httpApi={httpApi}
            userFirstName={user.firstName || ''}
            userEmail={user.email || ''}
            userPhone={user.phone || ''}
            withAgreement={!user.isAuthenticated}
            isPhone={isPhone}
            experiments={experiments}
          />
        </LoadableErrorBoundary>
      }
      footer={modalFooter}
      outside={false}
      escape={false}
      size="XS"
    />
  );
};
